import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const AUTOTASK_API_BASE = 'https://lively-dune-036b62e0f.4.azurestaticapps.net';

interface FeedbackData {
  id: string;
  description: string;
  screenshot: string | null;
  pageUrl: string;
  pageTitle: string;
  userName: string;
  submittedAt: string;
  status: string;
  reviewNotes: string;
  reviewedBy: string;
}

type OverlayState = 'loading' | 'error' | 'expanded' | 'minimized' | 'rejection-form' | 'closed';

interface FeedbackReviewOverlayProps {
  onActiveChange?: (isActive: boolean) => void;
}

export default function FeedbackReviewOverlay({ onActiveChange }: FeedbackReviewOverlayProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<OverlayState>('loading');
  const [feedback, setFeedback] = useState<FeedbackData | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const feedbackId = searchParams.get('reviewFeedback');
  const token = searchParams.get('token');
  const isActive = !!(feedbackId && token) && state !== 'closed';

  // Notify parent of active state changes
  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  // Clear URL params
  const clearUrlParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('reviewFeedback');
    newParams.delete('token');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Fetch feedback data
  useEffect(() => {
    if (!feedbackId || !token) {
      setState('closed');
      return;
    }

    const fetchFeedback = async () => {
      setState('loading');
      try {
        const response = await fetch(
          `${AUTOTASK_API_BASE}/api/feedback/review/${feedbackId}?token=${token}`
        );

        if (!response.ok) {
          if (response.status === 403) {
            setErrorMessage('This review link has expired. Please contact support.');
          } else if (response.status === 404) {
            setErrorMessage('Feedback not found.');
          } else {
            setErrorMessage(`Error loading feedback: ${response.status}`);
          }
          setState('error');
          return;
        }

        const data = await response.json();
        if (data.success && data.data) {
          setFeedback(data.data);
          setState('expanded');
        } else {
          setErrorMessage(data.error || 'Failed to load feedback');
          setState('error');
        }
      } catch (err) {
        console.error('Failed to fetch feedback:', err);
        setErrorMessage('Network error. Please try again.');
        setState('error');
      }
    };

    fetchFeedback();
  }, [feedbackId, token]);

  // Handle approve
  const handleApprove = async () => {
    if (!feedbackId || !token) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${AUTOTASK_API_BASE}/api/feedback/review/${feedbackId}/approve?token=${token}`,
        { method: 'POST' }
      );

      const data = await response.json();
      if (response.ok && data.success) {
        setToastMessage(data.message || 'Thank you! The fix has been confirmed.');
        setTimeout(() => {
          clearUrlParams();
          setState('closed');
        }, 2000);
      } else {
        setErrorMessage(data.error || 'Failed to submit approval');
        setState('error');
      }
    } catch (err) {
      console.error('Failed to approve:', err);
      setErrorMessage('Network error. Please try again.');
      setState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!feedbackId || !token || !rejectionReason.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${AUTOTASK_API_BASE}/api/feedback/review/${feedbackId}/reject?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: rejectionReason.trim() }),
        }
      );

      const data = await response.json();
      if (response.ok && data.success) {
        setToastMessage(data.message || "Thank you for your feedback. We'll take another look.");
        setTimeout(() => {
          clearUrlParams();
          setState('closed');
        }, 2000);
      } else {
        setErrorMessage(data.error || 'Failed to submit rejection');
        setState('error');
      }
    } catch (err) {
      console.error('Failed to reject:', err);
      setErrorMessage('Network error. Please try again.');
      setState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    clearUrlParams();
    setState('closed');
  };

  // Open screenshot in new tab
  const openScreenshot = () => {
    if (feedback?.screenshot) {
      const win = window.open();
      if (win) {
        win.document.write(`<img src="${feedback.screenshot}" style="max-width: 100%; height: auto;" />`);
        win.document.title = 'Feedback Screenshot';
      }
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Don't render if not active
  if (!isActive) return null;

  // Toast message
  if (toastMessage) {
    return (
      <div className="feedback-review-toast">
        <div className="feedback-review-toast-content">
          <span className="feedback-review-toast-icon">✓</span>
          {toastMessage}
        </div>
      </div>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <div className="feedback-review-overlay">
        <div className="feedback-review-modal">
          <div className="feedback-review-loading">
            <div className="loading-spinner"></div>
            <p>Loading feedback...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="feedback-review-overlay">
        <div className="feedback-review-modal feedback-review-modal-error">
          <div className="feedback-review-error">
            <span className="feedback-review-error-icon">⚠️</span>
            <p>{errorMessage}</p>
            <button className="feedback-review-btn" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Minimized state - floating bubbles
  if (state === 'minimized') {
    return (
      <div className="feedback-review-minimized">
        <button
          className="feedback-review-bubble feedback-review-bubble-approve"
          onClick={handleApprove}
          disabled={isSubmitting}
          title="It's Working!"
        >
          👍
        </button>
        <button
          className="feedback-review-bubble feedback-review-bubble-reject"
          onClick={() => setState('rejection-form')}
          disabled={isSubmitting}
          title="Still Not Working"
        >
          👎
        </button>
      </div>
    );
  }

  // Rejection form
  if (state === 'rejection-form') {
    return (
      <div className="feedback-review-overlay">
        <div className="feedback-review-modal">
          <div className="feedback-review-header">
            <h2>Tell Us What's Still Wrong</h2>
            <button
              className="feedback-review-close"
              onClick={handleClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <div className="feedback-review-body">
            <textarea
              className="feedback-review-textarea"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please describe what's still not working..."
              rows={5}
              disabled={isSubmitting}
            />
          </div>

          <div className="feedback-review-footer">
            <button
              className="feedback-review-btn feedback-review-btn-secondary"
              onClick={() => setState('expanded')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="feedback-review-btn feedback-review-btn-primary"
              onClick={handleReject}
              disabled={isSubmitting || !rejectionReason.trim()}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expanded state - full modal
  return (
    <div className="feedback-review-overlay">
      <div className="feedback-review-modal">
        <div className="feedback-review-header">
          <h2>Review Your Feedback</h2>
          <div className="feedback-review-header-actions">
            <button
              className="feedback-review-minimize"
              onClick={() => setState('minimized')}
              title="Minimize - test the page first"
            >
              _
            </button>
            <button
              className="feedback-review-close"
              onClick={handleClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>

        <div className="feedback-review-body">
          {/* Original submission */}
          <div className="feedback-review-section">
            <h3>Your original submission:</h3>
            <div className="feedback-review-card">
              <p className="feedback-review-description">{feedback?.description}</p>
              {feedback?.screenshot && (
                <div className="feedback-review-screenshot" onClick={openScreenshot}>
                  <img src={feedback.screenshot} alt="Original screenshot" />
                  <span className="feedback-review-screenshot-hint">Click to enlarge</span>
                </div>
              )}
              <div className="feedback-review-meta">
                <span>Submitted: {formatDate(feedback?.submittedAt || '')}</span>
                <span>Page: {feedback?.pageTitle || feedback?.pageUrl}</span>
              </div>
            </div>
          </div>

          {/* What was fixed */}
          <div className="feedback-review-section">
            <h3>What we fixed:</h3>
            <div className="feedback-review-card">
              <p className="feedback-review-notes">{feedback?.reviewNotes || 'No notes provided'}</p>
              {feedback?.reviewedBy && (
                <div className="feedback-review-meta">
                  <span>Reviewed by: {feedback.reviewedBy}</span>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <p className="feedback-review-instructions">
            Please test the page and let us know:
          </p>
        </div>

        <div className="feedback-review-footer">
          <button
            className="feedback-review-btn feedback-review-btn-approve"
            onClick={handleApprove}
            disabled={isSubmitting}
          >
            👍 It's Working!
          </button>
          <button
            className="feedback-review-btn feedback-review-btn-reject"
            onClick={() => setState('rejection-form')}
            disabled={isSubmitting}
          >
            👎 Still Not Working
          </button>
        </div>
      </div>
    </div>
  );
}
